import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AnimationController, ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { PlatformEnum, PlatformService } from '@panamax/app-state';
import {
  dualPaneDefaultVerifySave,
  getSearchableObservable$,
  multiSelectSearchDefault,
  updatePaneDirectlyWithValue,
  updateRightPaneWithValue,
} from '@shared/components/modal-with-nav-inline/dual-pane-container/Helpers/dual-pane-helper-functions';
import { BaseDualPaneModalContainerComponent } from '@shared/components/modal-with-nav-inline/dual-pane-container/base-dual-pane-modal-container/base-dual-pane-modal-container.component';
import {
  DualPaneView,
  MultiSelectItems,
} from '@shared/constants/dual-pane.enum';
import {
  BasicListItem,
  DualPaneFlagsConfig,
  DualPaneFunctionsConfig,
  DualPaneLabelsConfig,
  DualPaneViewModel,
  LeftPaneInfo,
  RightPaneInfo,
  SelectableItem,
} from '@shared/models/dual-pane-model';
import { ListKey } from '@usf/list-types';
import { LIST_ACTIONS, MassDeleteCustomerSummary } from '@usf/ngrx-list';
import { BehaviorSubject, Observable, firstValueFrom, map } from 'rxjs';
import { LoadingSpinnerService } from '../../../../shared/services/loading-spinner/loading-spinner.service';
import { DeleteListSummaryModalComponent } from '../../delete-list-summary-modal/delete-list-summary-modal.component';
import {
  DeleteListViewModel,
  DivisionNameNumber,
  MassDeleteCustomer,
} from '../models/delete-list.model';

@Component({
  selector: 'app-mass-delete-dual-pane-container',
  templateUrl: './mass-delete-dual-pane-container.component.html',
  styleUrls: ['./mass-delete-dual-pane-container.component.scss'],
})
export class MassDeleteDualPaneContainerComponent extends BaseDualPaneModalContainerComponent {
  @Output() submitEvent = new EventEmitter();
  @Input() massDeleteViewModel$: Observable<DeleteListViewModel>;
  @Input() isLoading$: Observable<boolean>;
  initialMarketItem: SelectableItem;
  initialCustomerItem: SelectableItem;
  @Input() submitOperation: Function = (valueMap: Map<string, any | any[]>) => {
    console.log(valueMap);
  };

  constructor(
    public platformService: PlatformService,
    public translateService: TranslateService,
    public datePipe: DatePipe,
    public modalController: ModalController,
    public store: Store,
    public loadingSpinnerService: LoadingSpinnerService,
    private animationCtrl: AnimationController,
  ) {
    super(platformService);
  }

  createVmForMassDelete = async () => {
    const massDeleteViewModel = await firstValueFrom(this.massDeleteViewModel$);
    this.initialMarketItem = this.setInitialMarketItem(massDeleteViewModel);
    this.initialCustomerItem = this.setInitialCustomerItem(massDeleteViewModel);

    const labelConfig: DualPaneLabelsConfig = {
      dataCy: 'mass-delete-modal',
      title: 'i18n.lists.deleteList',
      subTitle:
        this.translateService.instant(
          'i18n.lists.massDeleteModal.pleaseSelect',
        ) +
        massDeleteViewModel.listName +
        '.',
      submitText: this.translateService.instant('i18n.common.delete'),
      backText: this.translateService.instant('i18n.common.save'),
    };
    const functionConfig: DualPaneFunctionsConfig = {
      intialDesktopTabletView: this.buildMarketSelectionPane,
      handleSubmit: this.massDeleteHandleSubmit,
      verifySubmit: this.massDeleteVerifySubmit,
      overrideDismiss: this.onDismiss,
    };

    const flagConfig: DualPaneFlagsConfig = {};

    const contentItems = [
      {
        label: this.translateService.instant(
          'i18n.lists.massDeleteModal.markets',
        ),
        subLabel: this.initialMarketItem.label,
        placeHolder: this.translateService.instant(
          'i18n.lists.massDeleteModal.selectMarkets',
        ),
        view: DualPaneView.multiSelectCheckbox,
        selected: this.platformService.platformType !== PlatformEnum.mobile,
        onSelectfunction: this.buildMarketSelectionPane,
        value: [this.initialMarketItem],
      },
      {
        label: this.translateService.instant(
          'i18n.lists.massDeleteModal.customers',
        ),
        subLabel: this.initialCustomerItem.subLabel,
        placeHolder: this.translateService.instant(
          'i18n.lists.massDeleteModal.selectCustomers',
        ),
        view: DualPaneView.multiSelectCheckbox,
        selected: false,
        onSelectfunction: this.buildCustomerSelectionPane,
        value: [this.initialCustomerItem],
      },
    ];

    this.buildBasePaneWithConfigs(
      labelConfig,
      functionConfig,
      flagConfig,
      contentItems,
    );
  };

  buildMarketSelectionPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    let leftPaneValue = undefined;
    if (!!viewModel.leftView) {
      leftPaneValue = viewModel.leftView.content[0].value;
    } else {
      leftPaneValue = [this.initialMarketItem];
    }
    const marketSelectionModal = {
      view: DualPaneView.multiSelectCheckbox,
      title: this.translateService.instant(
        'i18n.lists.massDeleteModal.selectMarkets',
      ),
      itemsObservable$: this.getMarketsForMassDelete$(),
      handleItemSelection: this.marketMultiSelection,
      verifySaveButton: dualPaneDefaultVerifySave,
      searchPane: {
        hideSearch: true,
        searchPlaceholder: 'i18n.lists.copyListModal.searchForCustomers',
        onSearchFunction: multiSelectSearchDefault,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
      },
      countPane: {
        primaryCount: viewModel?.leftView?.content[0]?.value?.length ?? 1,
        secondaryCount: 0,
        selectableText: 'customerNumber',
      },
      overrideSaveOnNavigation: this.touchMultiOnBackButton,
    } as RightPaneInfo;
    marketSelectionModal.searchPane.searchableObservable$ =
      getSearchableObservable$(marketSelectionModal);
    updatePaneDirectlyWithValue(leftPaneValue, marketSelectionModal);
    return marketSelectionModal;
  };

  buildCustomerSelectionPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    let leftPaneValue = undefined;
    if (!!viewModel.leftView) {
      leftPaneValue = viewModel.leftView.content[1].value;
    } else {
      leftPaneValue = [this.customerMultiSelection];
    }
    const customerSelectionModal = {
      view: DualPaneView.multiSelectCheckbox,
      title: this.translateService.instant(
        'i18n.lists.massDeleteModal.selectCustomers',
      ),
      searchPane: {
        searchPlaceholder: 'i18n.lists.copyListModal.searchForCustomers',
        onSearchFunction: this.massDeleteCustomerSearch,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
      },
      countPane: {
        primaryCount: viewModel?.leftView?.content[1]?.value?.length,
        secondaryCount: 0,
        selectableText: 'customerNumber',
      },
      itemsObservable$: this.getCustomersForMassDelete$(),
      handleItemSelection: this.customerMultiSelection,
      verifySaveButton: dualPaneDefaultVerifySave,
      overrideSaveOnNavigation: this.touchMultiOnBackButton,
    } as RightPaneInfo;

    customerSelectionModal.searchPane.searchableObservable$ =
      getSearchableObservable$(customerSelectionModal);

    updatePaneDirectlyWithValue(leftPaneValue, customerSelectionModal);
    return customerSelectionModal;
  };

  async openModal() {
    await this.createVmForMassDelete();
    this.isModalOpen$.next(true);
  }

  closeModal() {
    this.isModalOpen$.next(false);
    if (this.submit$.value !== undefined) {
      this.submitOperation(this.submit$.value);
    }
  }

  getMarketsForMassDelete$() {
    return this.massDeleteViewModel$.pipe(
      map(massDeleteViewModel => {
        const leftPaneValue =
          this.dualPaneModalData.viewModel.leftView.content?.find(
            pane => pane.selected,
          );
        const marketItems: SelectableItem[] = [];
        massDeleteViewModel.markets?.forEach(market => {
          const isSelected = !!leftPaneValue.value?.find(
            (item: SelectableItem) =>
              item.value.divisionNumber === market.divisionNumber,
          );
          const marketItem: SelectableItem = {
            label: `${market.divisionName.toUpperCase()} (${
              market.divisionNumber
            })`,
            value: market,
            selected: isSelected,
            type: MultiSelectItems.basic,
          };
          marketItems.push(marketItem);
        });
        marketItems.sort((a, b) => {
          if (a.label > b.label) {
            return 1;
          }
          if (a.label < b.label) {
            return -1;
          }
          return 0;
        });
        return marketItems;
      }),
    );
  }

  getCustomersForMassDelete$() {
    return this.massDeleteViewModel$.pipe(
      map(massDeleteViewModel => {
        const leftPaneItem =
          this.dualPaneModalData.viewModel.leftView.content?.find(
            pane => pane.selected,
          );
        const marketLeftPane =
          this.dualPaneModalData.viewModel.leftView.content?.find(
            pane =>
              pane.label ===
              this.translateService.instant(
                'i18n.lists.massDeleteModal.markets',
              ),
          );
        const customerItems: SelectableItem[] = [];
        marketLeftPane.value?.forEach((marketItem: SelectableItem) => {
          const divisionNameNumber: DivisionNameNumber = marketItem.value;
          const massDeleteCustomers: MassDeleteCustomer[] =
            massDeleteViewModel.marketCustomerMap.get(
              divisionNameNumber.divisionNumber,
            ) ?? [];
          massDeleteCustomers.forEach(massDeleteCustomer => {
            const isCustSelected: boolean =
              !massDeleteCustomer.isHeader &&
              !!leftPaneItem.value?.find(
                (item: SelectableItem) =>
                  item.value?.customerNumber ===
                    massDeleteCustomer.customerNumber &&
                  item.value?.departmentNumber ===
                    massDeleteCustomer.departmentNumber,
              );
            const customerItem: SelectableItem = {
              label: massDeleteCustomer.isDepartment
                ? `${massDeleteCustomer.departmentName} (${massDeleteCustomer.departmentNumber})`
                : `${massDeleteCustomer.customerName} (${massDeleteCustomer.customerNumber})`,
              value: massDeleteCustomer,
              selected: isCustSelected,
              type: massDeleteCustomer.isHeader
                ? MultiSelectItems.iconHeader
                : massDeleteCustomer.isDepartment
                  ? MultiSelectItems.subItem
                  : MultiSelectItems.iconBasic,
              subLabel: massDeleteCustomer.customerNumber.toString(),
            };
            customerItems.push(customerItem);
          });
        });
        customerItems.sort((a, b) => {
          const customerA = a.value.customerName.toUpperCase();
          const customerB = b.value.customerName.toUpperCase();
          if (customerA > customerB) {
            return 1;
          }
          if (customerA < customerB) {
            return -1;
          }
          return 0;
        });
        return customerItems;
      }),
    );
  }

  marketMultiSelection = (
    left: LeftPaneInfo,
    platform: PlatformEnum,
    right: RightPaneInfo,
    marketItemsArray: SelectableItem[],
    selectedCount: string,
    selectableItem?: SelectableItem,
  ) => {
    // set customer sublabel in accordance with the deselection of markets
    // that were tied to any selected customers
    const customerPane = left.content[1];
    if (selectableItem && !selectableItem.selected) {
      customerPane.value = customerPane.value.filter(
        customerItem =>
          customerItem.value.divisionNumber !==
          selectableItem.value.divisionNumber,
      );
      if (customerPane.value.length === 1) {
        if (customerPane.value[0].type === MultiSelectItems.subItem) {
          customerPane.subLabel = `${customerPane.value[0].value.customerName} (${customerPane.value[0].value.customerNumber}) ${customerPane.value[0].value.departmentName} (${customerPane.value[0].value.departmentNumber})`;
        } else {
          customerPane.subLabel = `${customerPane.value[0].value.customerName} (${customerPane.value[0].value.customerNumber})`;
        }
      } else if (customerPane.value.length > 0) {
        customerPane.subLabel =
          this.translateService.instant('i18n.lists.massDeleteModal.selected') +
          customerPane.value.length;
      } else {
        customerPane.subLabel = this.translateService.instant(
          'i18n.lists.massDeleteModal.selectCustomers',
        );
      }
    }
    // set customer sublabel on deselect all markets
    if (!selectableItem && marketItemsArray.length === 0) {
      customerPane.subLabel = this.translateService.instant(
        'i18n.lists.massDeleteModal.selectCustomers',
      );
      customerPane.value = [];
    }

    left.content = left.content.map(listItem => {
      if (listItem.selected) {
        if (marketItemsArray.length === 1) {
          const sublabel = `${marketItemsArray[0].value.divisionName} (${marketItemsArray[0].value.divisionNumber})`;
          return {
            ...listItem,
            subLabel: sublabel,
            value: marketItemsArray,
          };
        } else if (marketItemsArray.length === 0) {
          return {
            ...listItem,
            subLabel: this.translateService.instant(
              'i18n.lists.massDeleteModal.selectMarkets',
            ),
            value: marketItemsArray,
          };
        }
        return {
          ...listItem,
          subLabel: selectedCount,
          value: marketItemsArray,
        };
      } else {
        return listItem;
      }
    });
    if (right.view !== DualPaneView.noView) {
      right.value = marketItemsArray;
    }
  };

  customerMultiSelection = (
    left: LeftPaneInfo,
    platform: PlatformEnum,
    right: RightPaneInfo,
    customerItemsArray: SelectableItem[],
    selectedCount: string,
    selectableItem?: SelectableItem,
  ) => {
    left.content = left.content.map(listItem => {
      if (listItem.selected) {
        if (customerItemsArray.length === 1) {
          let sublabel = '';
          if (customerItemsArray[0].value.isDepartment) {
            sublabel = `${customerItemsArray[0].value.customerName} (${customerItemsArray[0].value.customerNumber}) ${customerItemsArray[0].value.departmentName} (${customerItemsArray[0].value.departmentNumber})`;
          } else {
            sublabel = `${customerItemsArray[0].value.customerName} (${customerItemsArray[0].value.customerNumber})`;
          }
          return {
            ...listItem,
            subLabel: sublabel,
            value: customerItemsArray,
          };
        } else if (customerItemsArray.length === 0) {
          return {
            ...listItem,
            subLabel: this.translateService.instant(
              'i18n.lists.massDeleteModal.selectCustomers',
            ),
            value: customerItemsArray,
          };
        }
        return {
          ...listItem,
          subLabel: selectedCount,
          value: customerItemsArray,
        };
      } else {
        return listItem;
      }
    });
    if (right.view !== DualPaneView.noView) {
      right.value = customerItemsArray;
    }
  };

  touchMultiOnBackButton = (
    viewModel: DualPaneViewModel,
    leftPaneItem: BasicListItem,
    value: SelectableItem[],
  ) => {
    const isMarketPane =
      leftPaneItem.label ===
      this.translateService.instant('i18n.lists.massDeleteModal.markets');
    let selectedString = isMarketPane
      ? this.translateService.instant(
          'i18n.lists.massDeleteModal.selectMarkets',
        )
      : this.translateService.instant(
          'i18n.lists.massDeleteModal.selectCustomers',
        );
    if (value.length === 1 && !isMarketPane) {
      if (value[0].value.isDepartment) {
        selectedString = `${value[0].value.customerName} (${value[0].value.customerNumber}) ${value[0].value.departmentName} (${value[0].value.departmentNumber})`;
      } else {
        selectedString = `${value[0].value.customerName} (${value[0].value.customerNumber})`;
      }
    } else if (value.length === 1) {
      selectedString = value[0].label;
    } else if (value.length > 1) {
      selectedString =
        this.translateService.instant('i18n.lists.massDeleteModal.selected') +
        value.length;
    }
    return { ...leftPaneItem, subLabel: selectedString, value };
  };

  setInitialMarketItem = (
    massDeleteViewModel: DeleteListViewModel,
  ): SelectableItem => {
    const initialMarket: DivisionNameNumber = {
      divisionName: massDeleteViewModel.initialMassDeleteCustomer.divisionName,
      divisionNumber:
        massDeleteViewModel.initialMassDeleteCustomer.divisionNumber,
    };
    const label = `${initialMarket.divisionName} (${initialMarket.divisionNumber})`;
    return {
      label,
      value: initialMarket,
      selected: true,
      type: MultiSelectItems.basic,
    };
  };

  setInitialCustomerItem = (
    massDeleteViewModel: DeleteListViewModel,
  ): SelectableItem => {
    const isDept = massDeleteViewModel.initialMassDeleteCustomer.isDepartment;
    const custName =
      massDeleteViewModel?.initialMassDeleteCustomer?.customerName;
    const custNumber =
      massDeleteViewModel?.initialMassDeleteCustomer?.customerNumber;
    const deptName =
      massDeleteViewModel?.initialMassDeleteCustomer?.departmentName;
    const deptNumber =
      massDeleteViewModel?.initialMassDeleteCustomer?.departmentNumber;
    return {
      label: isDept
        ? `${deptName} (${deptNumber})`
        : `${custName} (${custNumber})`,
      subLabel: isDept
        ? `${custName} (${custNumber}) ${deptName} (${deptNumber})`
        : `${custName} (${custNumber})`,
      value: massDeleteViewModel.initialMassDeleteCustomer,
      selected: true,
      type: isDept ? MultiSelectItems.subItem : MultiSelectItems.iconBasic,
    };
  };

  massDeleteVerifySubmit = (vm: DualPaneViewModel) => {
    let valid = true;
    vm.leftView.content.forEach(item => {
      if (item.value.length < 1) {
        valid = false;
      }
    });
    return valid;
  };

  massDeleteHandleSubmit = async (vm: DualPaneViewModel) => {
    const massDeleteViewModel = await firstValueFrom(this.massDeleteViewModel$);
    const customerArray = vm.leftView.content[1].value;
    const listKeys: ListKey[] = [];
    const customerSummaryMap: Map<number, MassDeleteCustomerSummary> =
      new Map();
    customerArray?.forEach(customerItem => {
      const massDeleteCustomer: MassDeleteCustomer = customerItem.value;
      listKeys.push(massDeleteCustomer.listKey);
      if (customerSummaryMap.has(massDeleteCustomer.customerNumber)) {
        const massDeleteCustomerSummary = customerSummaryMap.get(
          massDeleteCustomer.customerNumber,
        );
        customerSummaryMap.set(massDeleteCustomer.customerNumber, {
          customerName: massDeleteCustomer.customerName,
          customerNumber: massDeleteCustomer.customerNumber,
          departments: [
            ...massDeleteCustomerSummary.departments,
            massDeleteCustomer.departmentName +
              ' (' +
              massDeleteCustomer.departmentNumber +
              ')',
          ],
        } as MassDeleteCustomerSummary);
      } else {
        const departments = [];
        if (massDeleteCustomer.departmentName) {
          departments.push(
            massDeleteCustomer.departmentName +
              ' (' +
              massDeleteCustomer.departmentNumber +
              ')',
          );
        }
        customerSummaryMap.set(massDeleteCustomer.customerNumber, {
          customerName: massDeleteCustomer.customerName,
          customerNumber: massDeleteCustomer.customerNumber,
          departments,
        } as MassDeleteCustomerSummary);
      }
    });
    const customerList = [...customerSummaryMap.values()];
    const leaveAnimation = () => {
      const animation = this.animationCtrl
        .create()
        .addElement(document.querySelector('#mass-delete-summary'))
        .fromTo('opacity', '1', '0');

      return this.animationCtrl
        .create()
        .addElement(document.querySelector('#mass-delete-summary'))
        .easing('ease-in')
        .duration(100)
        .addAnimation([animation]);
    };
    const summaryModal = await this.modalController.create({
      component: DeleteListSummaryModalComponent,
      componentProps: {
        customerList,
        title: this.translateService.instant(
          'i18n.lists.massDeleteModal.confirmationTitle',
          { listName: massDeleteViewModel.listName },
        ),
        platform: this.platformService.platformType,
        isConfirmation: true,
      },
      cssClass:
        this.platformService.platformType ===
        this.platformService.platformEnum.desktop
          ? 'modal-desktop-size-xl'
          : 'ion-modal-touch',
      mode: 'ios',
      id: 'mass-delete-summary',
      initialBreakpoint:
        this.platformService.platformType !==
        this.platformService.platformEnum.desktop
          ? 1
          : undefined,
      breakpoints: [0, 1],
      enterAnimation: () => this.animationCtrl.create(),
      leaveAnimation,
    });
    summaryModal.present();
    summaryModal.onDidDismiss().then(data => {
      if (data.data) {
        this.dualPaneModalData.modalOpenState$.next(false);
        this.loadingSpinnerService.createSpinnerModal().then(() => {
          this.store.dispatch(
            LIST_ACTIONS.massDelete({
              listName: massDeleteViewModel.listName,
              listKeys,
              customerList,
            }),
          );
        });
      }
    });
  };

  massDeleteCustomerSearch = (
    rightPane: RightPaneInfo,
    searchKey: string,
    items: SelectableItem[],
  ) => {
    const searchItems = items.filter(item => {
      return (
        item?.value?.customerName
          .toLowerCase()
          ?.includes((searchKey || '').toLowerCase()) ||
        item?.value?.customerNumber
          .toString()
          .includes((searchKey || '').toLowerCase())
      );
    });
    const filteredItems = searchItems.filter(
      item => item.type !== MultiSelectItems.iconHeader,
    );
    rightPane.countPane.secondaryCount = filteredItems.length;
    rightPane.countPane.primaryCount = filteredItems.filter(
      filtItem => filtItem.selected,
    ).length;
    return searchItems;
  };

  onDismiss = () => {
    this.dualPaneModalData.modalOpenState$.next(false);
  };
}
